import React, { useEffect, useMemo, useState } from 'react';
import {
  getAssetUrl,
  getSuperAdminSupportFeedback,
  getSuperAdminSupportTicket,
  getSuperAdminSupportTickets,
  replySuperAdminSupportTicket,
  resolveSuperAdminSupportTicket,
  updateSuperAdminSupportTicket,
} from '../services/api';
import SupportAttachmentPreview, { SupportAttachmentPreviewState } from '../components/SupportAttachmentPreview';
import SupportResolutionPanel from '../components/SupportResolutionPanel';
import {
  SupportFeedback,
  SupportResolutionTag,
  SupportTicket,
  SupportTicketCategory,
  SupportTicketPriority,
  SupportTicketResolution,
  SupportTicketStatus,
  SupportTicketThread,
  Tenant,
} from '../types';

const STATUSES: Array<{ value: '' | SupportTicketStatus; label: string }> = [
  { value: '', label: 'All statuses' },
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'waiting_for_customer', label: 'Waiting for customer' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' },
];

const PRIORITIES: Array<{ value: '' | SupportTicketPriority; label: string }> = [
  { value: '', label: 'All priorities' },
  { value: 'urgent', label: 'Urgent' },
  { value: 'high', label: 'High' },
  { value: 'normal', label: 'Normal' },
  { value: 'low', label: 'Low' },
];

const CATEGORIES: Array<{ value: '' | SupportTicketCategory; label: string }> = [
  { value: '', label: 'All categories' },
  { value: 'device_data', label: 'Device / data issue' },
  { value: 'bug', label: 'Bug' },
  { value: 'billing', label: 'Billing / plan' },
  { value: 'feature_request', label: 'Feature request' },
  { value: 'general', label: 'General inquiry' },
];

const STATUS_COPY: Record<SupportTicketStatus, string> = {
  open: 'Open',
  in_progress: 'In progress',
  waiting_for_customer: 'Waiting for customer',
  resolved: 'Resolved',
  closed: 'Closed',
};

const CATEGORY_COPY: Record<SupportTicketCategory, string> = {
  bug: 'Bug',
  feature_request: 'Feature request',
  billing: 'Billing / plan',
  device_data: 'Device / data issue',
  general: 'General inquiry',
};

const PRIORITY_COPY: Record<SupportTicketPriority, string> = {
  low: 'Low',
  normal: 'Normal',
  high: 'High',
  urgent: 'Urgent',
};

const RESOLUTION_TAGS: Array<{ value: SupportResolutionTag; label: string }> = [
  { value: 'dashboard', label: 'Dashboard' },
  { value: 'device', label: 'Device' },
  { value: 'login', label: 'Login' },
  { value: 'billing', label: 'Billing' },
  { value: 'sensor_data', label: 'Sensor data' },
  { value: 'report', label: 'Report' },
  { value: 'mqtt', label: 'MQTT' },
  { value: 'account', label: 'Account' },
  { value: 'other', label: 'Other' },
];

type ResolveMode = 'resolved' | 'closed';

type ResolutionForm = {
  customer_issue: string;
  root_cause: string;
  fix_applied: string;
  prevention_note: string;
  internal_notes: string;
  customer_resolution: string;
  tags: SupportResolutionTag[];
  kb_candidate: boolean;
};

const EMPTY_RESOLUTION_FORM: ResolutionForm = {
  customer_issue: '',
  root_cause: '',
  fix_applied: '',
  prevention_note: '',
  internal_notes: '',
  customer_resolution: '',
  tags: [],
  kb_candidate: false,
};

type Filters = {
  tenant_id: string;
  status: '' | SupportTicketStatus;
  priority: '' | SupportTicketPriority;
  category: '' | SupportTicketCategory;
  q: string;
};

const EMPTY_FILTERS: Filters = {
  tenant_id: '',
  status: '',
  priority: '',
  category: '',
  q: '',
};

export default function SuperAdminSupport({ tenants }: { tenants: Tenant[] }) {
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [feedback, setFeedback] = useState<SupportFeedback[]>([]);
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [selectedId, setSelectedId] = useState('');
  const [thread, setThread] = useState<SupportTicketThread | null>(null);
  const [replyBody, setReplyBody] = useState('');
  const [assignedTo, setAssignedTo] = useState('');
  const [loading, setLoading] = useState(false);
  const [threadLoading, setThreadLoading] = useState(false);
  const [action, setAction] = useState('');
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const [preview, setPreview] = useState<SupportAttachmentPreviewState | null>(null);
  const [resolveMode, setResolveMode] = useState<ResolveMode | null>(null);
  const [resolutionForm, setResolutionForm] = useState<ResolutionForm>(EMPTY_RESOLUTION_FORM);
  const [resolutionError, setResolutionError] = useState('');

  useEffect(() => {
    loadTickets();
    loadFeedback();
  }, []);

  useEffect(() => {
    if (!selectedId) {
      setThread(null);
      return;
    }
    loadThread(selectedId);
  }, [selectedId]);

  useEffect(() => {
    setAssignedTo(thread?.ticket.assigned_to || '');
  }, [thread?.ticket.assigned_to, thread?.ticket.id]);

  const activeTickets = useMemo(() => tickets.filter((ticket) => !['resolved', 'closed'].includes(ticket.status)).length, [tickets]);
  const urgentTickets = useMemo(() => tickets.filter((ticket) => ticket.priority === 'urgent').length, [tickets]);
  const waitingTickets = useMemo(() => tickets.filter((ticket) => ticket.status === 'waiting_for_customer').length, [tickets]);
  const activeTicketList = useMemo(() => tickets.filter((ticket) => ticket.status !== 'closed'), [tickets]);
  const archivedTicketList = useMemo(() => tickets.filter((ticket) => ticket.status === 'closed'), [tickets]);
  const selectedThread = thread?.ticket.id === selectedId ? thread : null;
  const selectedTicket = selectedThread?.ticket || tickets.find((ticket) => ticket.id === selectedId) || null;
  const selectedResolution = selectedThread?.resolution || null;

  async function loadTickets(nextFilters = filters): Promise<SupportTicket[]> {
    setLoading(true);
    setError('');
    try {
      const next = await getSuperAdminSupportTickets(nextFilters);
      const firstActive = next.find((ticket) => ticket.status !== 'closed');
      setTickets(next);
      if (!selectedId && firstActive) setSelectedId(firstActive.id);
      if (selectedId && !next.some((ticket) => ticket.id === selectedId)) {
        setSelectedId(firstActive?.id || '');
      }
      return next;
    } catch (err: any) {
      setError(err.response?.data?.error || 'Unable to load support tickets.');
      return [];
    } finally {
      setLoading(false);
    }
  }

  async function loadFeedback() {
    try {
      setFeedback(await getSuperAdminSupportFeedback());
    } catch (err: any) {
      setError(err.response?.data?.error || 'Unable to load feedback.');
    }
  }

  async function loadThread(id: string) {
    setThreadLoading(true);
    setThread(null);
    setError('');
    try {
      setThread(await getSuperAdminSupportTicket(id));
    } catch (err: any) {
      setError(err.response?.data?.error || 'Unable to load ticket thread.');
    } finally {
      setThreadLoading(false);
    }
  }

  function updateFilter<K extends keyof Filters>(key: K, value: Filters[K]) {
    setFilters((current) => ({ ...current, [key]: value }));
  }

  async function handleApplyFilters(event: React.FormEvent) {
    event.preventDefault();
    await loadTickets(filters);
  }

  async function handleReply(event: React.FormEvent) {
    event.preventDefault();
    if (!selectedTicket || !replyBody.trim()) return;
    setAction('reply');
    setError('');
    setNotice('');
    try {
      await replySuperAdminSupportTicket(selectedTicket.id, { body: replyBody.trim() });
      setReplyBody('');
      setNotice(`Reply sent for ${getTicketNumber(selectedTicket)}.`);
      await Promise.all([loadTickets(), loadThread(selectedTicket.id)]);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Unable to send reply.');
    } finally {
      setAction('');
    }
  }

  async function handleUpdateTicket(data: { status?: SupportTicketStatus; priority?: SupportTicketPriority; assigned_to?: string | null }) {
    if (!selectedTicket) return;
    setAction('update');
    setError('');
    setNotice('');
    try {
      const result = await updateSuperAdminSupportTicket(selectedTicket.id, data);
      setNotice(`${getTicketNumber(result.ticket)} updated.`);
      setTickets((current) => current.map((ticket) => (ticket.id === result.ticket.id ? { ...ticket, ...result.ticket } : ticket)));
      if (data.status === 'closed') {
        const next = await loadTickets();
        const nextActive = next.find((ticket) => ticket.id !== selectedTicket.id && ticket.status !== 'closed');
        setSelectedId(nextActive?.id || '');
        setThread(null);
      } else {
        await loadThread(selectedTicket.id);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Unable to update ticket.');
    } finally {
      setAction('');
    }
  }

  function handleStatusChange(status: SupportTicketStatus) {
    if (!selectedTicket) return;
    if ((status === 'resolved' || status === 'closed') && !selectedResolution) {
      openResolveModal(status);
      return;
    }
    handleUpdateTicket({ status });
  }

  function handleCloseTicket() {
    if (!selectedTicket) return;
    if (!selectedResolution) {
      openResolveModal('closed');
      return;
    }
    handleUpdateTicket({ status: 'closed' });
  }

  function openResolveModal(mode: ResolveMode) {
    if (!selectedTicket) return;
    setResolutionForm(makeResolutionForm(selectedTicket, selectedResolution));
    setResolutionError('');
    setError('');
    setNotice('');
    setResolveMode(mode);
  }

  async function handleResolveTicket(event: React.FormEvent) {
    event.preventDefault();
    if (!selectedTicket || !resolveMode) return;

    const rootCause = resolutionForm.root_cause.trim();
    const fixApplied = resolutionForm.fix_applied.trim();
    const customerResolution = resolutionForm.customer_resolution.trim();
    if (!rootCause || !fixApplied || !customerResolution) {
      setResolutionError('Add the root cause, fix applied, and customer-facing resolution before saving.');
      return;
    }

    setAction('resolve');
    setResolutionError('');
    setError('');
    setNotice('');
    try {
      const result = await resolveSuperAdminSupportTicket(selectedTicket.id, {
        customer_issue: resolutionForm.customer_issue.trim() || selectedTicket.subject,
        root_cause: rootCause,
        fix_applied: fixApplied,
        prevention_note: resolutionForm.prevention_note.trim() || null,
        internal_notes: resolutionForm.internal_notes.trim() || null,
        customer_resolution: customerResolution,
        tags: resolutionForm.tags,
        kb_candidate: resolutionForm.kb_candidate,
      });

      setTickets((current) => current.map((ticket) => (ticket.id === result.ticket.id ? { ...ticket, ...result.ticket } : ticket)));

      if (resolveMode === 'closed') {
        const closed = await updateSuperAdminSupportTicket(selectedTicket.id, { status: 'closed' });
        setTickets((current) => current.map((ticket) => (ticket.id === closed.ticket.id ? { ...ticket, ...closed.ticket } : ticket)));
        setResolveMode(null);
        setNotice(`${getTicketNumber(closed.ticket)} resolved and closed.`);
        const next = await loadTickets();
        const nextActive = next.find((ticket) => ticket.id !== selectedTicket.id && ticket.status !== 'closed');
        setSelectedId(nextActive?.id || '');
        setThread(null);
        return;
      }

      setResolveMode(null);
      setNotice(`${getTicketNumber(result.ticket)} resolved with a structured summary.`);
      await Promise.all([loadTickets(), loadThread(selectedTicket.id)]);
    } catch (err: any) {
      setResolutionError(err.response?.data?.error || 'Unable to resolve ticket.');
    } finally {
      setAction('');
    }
  }

  function handlePreviewAttachment(url: string, title: string) {
    setPreview({
      alt: title,
      src: getAssetUrl(url) || url,
      title,
    });
  }

  return (
    <>
    <section className="sd-super-support-page sd-support-command-page">
      {(notice || error) && (
        <div className={`sd-inline-alert ${error ? 'is-danger' : 'is-success'} sd-super-notice`}>
          <span>{error || notice}</span>
          <button onClick={() => { setNotice(''); setError(''); }} type="button">Dismiss</button>
        </div>
      )}

      <section className="sd-command-tab-summary">
        <div className="sd-command-tab-copy">
          <span>Support</span>
          <h2>Ticket Queue</h2>
          <p>Prioritize tickets, replies, assignments, and tenant context from one command workspace.</p>
        </div>
        <div className="sd-command-micro-metrics" aria-label="Support queue metrics">
          <Stat label="Active" value={activeTickets} detail="Open work" tone="blue" />
          <Stat label="Urgent" value={urgentTickets} detail="Fastest lane" tone="danger" />
          <Stat label="Waiting" value={waitingTickets} detail="Customer action" tone="warning" />
          <Stat label="Feedback" value={feedback.length} detail="Recent ratings" tone="green" />
        </div>
      </section>

      <article className="sd-panel sd-support-command-filters">
        <form onSubmit={handleApplyFilters}>
          <label>
            <span>Tenant</span>
            <select value={filters.tenant_id} onChange={(event) => updateFilter('tenant_id', event.target.value)}>
              <option value="">All tenants</option>
              {tenants.map((tenant) => <option key={tenant.id} value={tenant.id}>{tenant.name}</option>)}
            </select>
          </label>
          <label>
            <span>Status</span>
            <select value={filters.status} onChange={(event) => updateFilter('status', event.target.value as Filters['status'])}>
              {STATUSES.map((status) => <option key={status.value || 'all'} value={status.value}>{status.label}</option>)}
            </select>
          </label>
          <label>
            <span>Priority</span>
            <select value={filters.priority} onChange={(event) => updateFilter('priority', event.target.value as Filters['priority'])}>
              {PRIORITIES.map((priority) => <option key={priority.value || 'all'} value={priority.value}>{priority.label}</option>)}
            </select>
          </label>
          <label>
            <span>Category</span>
            <select value={filters.category} onChange={(event) => updateFilter('category', event.target.value as Filters['category'])}>
              {CATEGORIES.map((category) => <option key={category.value || 'all'} value={category.value}>{category.label}</option>)}
            </select>
          </label>
          <label className="sd-command-search-field">
            <span>Search</span>
            <input value={filters.q} onChange={(event) => updateFilter('q', event.target.value)} placeholder="Ticket number, tenant, or subject" />
          </label>
          <button className="sd-primary-button" disabled={loading} type="submit">{loading ? 'Loading...' : 'Filter'}</button>
        </form>
      </article>

      <div className="sd-support-command-grid">
        <aside className="sd-panel sd-command-queue">
          <div className="sd-command-panel-header">
            <div>
              <h2>Ticket Queue</h2>
              <p>{tickets.length} result{tickets.length === 1 ? '' : 's'} in the current view.</p>
            </div>
            <button className="sd-secondary-button" disabled={loading} onClick={() => loadTickets()} type="button">Refresh</button>
          </div>
          {loading ? (
            <EmptyDeskState title="Loading support tickets..." detail="Refreshing the customer queue." />
          ) : tickets.length === 0 ? (
            <EmptyDeskState title="No tickets match this view" detail="Adjust the filters or wait for new customer requests." />
          ) : (
            <div className="sd-command-ticket-stack">
              {activeTicketList.length === 0 ? (
                <EmptyDeskState title="No active tickets" detail="Closed tickets are available in the archive below." compact />
              ) : (
                <TicketQueueList tickets={activeTicketList} selectedId={selectedId} onSelect={setSelectedId} />
              )}
              {archivedTicketList.length > 0 && (
                <details className="sd-support-archive">
                  <summary>Closed / Archive <span>{archivedTicketList.length}</span></summary>
                  <TicketQueueList tickets={archivedTicketList} selectedId={selectedId} onSelect={setSelectedId} />
                </details>
              )}
            </div>
          )}
        </aside>

        <main className="sd-panel sd-command-thread">
          <div className="sd-command-panel-header sd-command-thread-header">
            <div>
              <span className="sd-command-ticket-number">{selectedTicket ? getTicketNumber(selectedTicket) : 'No ticket selected'}</span>
              <h2>{selectedTicket ? selectedTicket.subject : 'Conversation Thread'}</h2>
              <p>{selectedTicket ? `${selectedTicket.tenant_name || 'Tenant'} - ${CATEGORY_COPY[selectedTicket.category]}` : 'Select a ticket from the queue.'}</p>
            </div>
            {selectedTicket && (
              <div className="sd-command-thread-badges">
                <span className={`sd-support-status is-${selectedTicket.status}`}>{STATUS_COPY[selectedTicket.status]}</span>
                <PriorityBadge priority={selectedTicket.priority} />
              </div>
            )}
          </div>

          {threadLoading ? (
            <EmptyDeskState title="Loading ticket thread..." detail="Fetching customer and support replies." />
          ) : selectedThread ? (
            <>
              <div className="sd-support-message-list sd-command-message-list">
                {selectedThread.messages.map((message) => <SupportMessage key={message.id} message={message} onPreview={handlePreviewAttachment} />)}
              </div>

              {selectedThread.resolution && <SupportResolutionPanel resolution={selectedThread.resolution} variant="superadmin" />}

              {selectedThread.ticket.status === 'closed' ? (
                <div className="sd-inline-alert">This archived ticket is closed. Reopen from the context panel if support needs to continue.</div>
              ) : (
                <form className="sd-command-reply" onSubmit={handleReply}>
                  <label className="sd-field">
                    <span>Support Reply</span>
                    <textarea rows={5} value={replyBody} onChange={(event) => setReplyBody(event.target.value)} placeholder="Reply as Seedronics support. The ticket owner receives an email notification." />
                  </label>
                  <div className="sd-command-reply-actions">
                    <span>{selectedTicket ? `Replying to ${getTicketNumber(selectedTicket)}` : 'Select a ticket'}</span>
                    <button className="sd-primary-button" disabled={action === 'reply' || !replyBody.trim()} type="submit">
                      {action === 'reply' ? 'Sending...' : 'Send Reply'}
                    </button>
                  </div>
                </form>
              )}
            </>
          ) : (
            <EmptyDeskState title="Select a ticket" detail="The conversation, attachments, and reply composer will appear here." />
          )}
        </main>

        <aside className="sd-panel sd-command-context">
          <div className="sd-command-panel-header">
            <div>
              <h2>Tenant Context</h2>
              <p>Status, owner, customer, and operational handoff.</p>
            </div>
          </div>
          {selectedTicket ? (
            <>
              <div className="sd-command-context-card">
                <span>Ticket Number</span>
                <strong>{getTicketNumber(selectedTicket)}</strong>
                <small>{selectedTicket.ticket_number ? 'Backend generated' : 'Temporary UUID fallback'}</small>
              </div>

              <div className="sd-command-context-list">
                <ContextRow label="Tenant" value={selectedTicket.tenant_name || 'Unknown tenant'} />
                <ContextRow label="Requester" value={selectedTicket.requester_name || selectedTicket.requester_email || 'Unknown requester'} />
                <ContextRow label="Requester email" value={selectedTicket.requester_email || '-'} />
                <ContextRow label="Category" value={CATEGORY_COPY[selectedTicket.category]} />
                <ContextRow label="Messages" value={`${selectedTicket.message_count || selectedThread?.messages.length || 0}`} />
                <ContextRow label="Created" value={formatDate(selectedTicket.created_at)} />
                <ContextRow label="Last update" value={formatDate(selectedTicket.updated_at)} />
              </div>

              <div className="sd-command-control-stack">
                <label>
                  <span>Status</span>
                  <select
                    disabled={action === 'update'}
                    value={selectedTicket.status}
                    onChange={(event) => handleStatusChange(event.target.value as SupportTicketStatus)}
                  >
                    {STATUSES.filter((status) => status.value).map((status) => <option key={status.value} value={status.value}>{status.label}</option>)}
                  </select>
                </label>
                <label>
                  <span>Priority</span>
                  <select
                    disabled={action === 'update'}
                    value={selectedTicket.priority}
                    onChange={(event) => handleUpdateTicket({ priority: event.target.value as SupportTicketPriority })}
                  >
                    {PRIORITIES.filter((priority) => priority.value).map((priority) => <option key={priority.value} value={priority.value}>{priority.label}</option>)}
                  </select>
                </label>
                <label>
                  <span>Assigned to</span>
                  <input value={assignedTo} onChange={(event) => setAssignedTo(event.target.value)} placeholder="support owner" />
                </label>
                <button
                  className="sd-secondary-button"
                  disabled={action === 'update'}
                  onClick={() => handleUpdateTicket({ assigned_to: assignedTo.trim() || null })}
                  type="button"
                >
                  Save Assignment
                </button>
              </div>

              <div className="sd-command-action-grid">
                {selectedTicket.status !== 'closed' && (
                  <button disabled={action === 'update'} onClick={() => openResolveModal('resolved')} type="button">
                    {selectedResolution ? 'Update Resolution' : 'Resolve with Summary'}
                  </button>
                )}
                {selectedTicket.status !== 'closed' ? (
                  <button className="is-danger" disabled={action === 'update'} onClick={handleCloseTicket} type="button">
                    Close Ticket
                  </button>
                ) : (
                  <button disabled={action === 'update'} onClick={() => handleUpdateTicket({ status: 'open' })} type="button">
                    Reopen Ticket
                  </button>
                )}
              </div>
            </>
          ) : (
            <EmptyDeskState title="No ticket selected" detail="Select a queue item to review tenant context and actions." compact />
          )}
        </aside>
      </div>

      <article className="sd-panel sd-command-feedback-panel">
        <div className="sd-command-panel-header">
          <div>
            <h2>Recent Feedback</h2>
            <p>Quick tenant ratings and product comments from the support widget.</p>
          </div>
          <button className="sd-secondary-button" onClick={loadFeedback} type="button">Refresh Feedback</button>
        </div>
        <div className="sd-table-wrap">
          <table className="sd-table">
            <thead>
              <tr>
                <th>Tenant</th>
                <th>User</th>
                <th>Rating</th>
                <th>Page</th>
                <th>Comment</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {feedback.length === 0 ? (
                <tr><td className="sd-empty-row" colSpan={6}>No feedback yet.</td></tr>
              ) : feedback.map((entry) => (
                <tr key={entry.id}>
                  <td>{entry.tenant_name || '-'}</td>
                  <td>{entry.user_email || '-'}</td>
                  <td>{entry.rating}/5</td>
                  <td>{entry.page || '-'}</td>
                  <td>{entry.comment || '-'}</td>
                  <td>{formatDate(entry.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </article>
    </section>
    {resolveMode && selectedTicket && (
      <ResolveTicketModal
        action={action}
        error={resolutionError}
        form={resolutionForm}
        mode={resolveMode}
        onChange={setResolutionForm}
        onClose={() => setResolveMode(null)}
        onSubmit={handleResolveTicket}
        ticket={selectedTicket}
      />
    )}
    <SupportAttachmentPreview preview={preview} onClose={() => setPreview(null)} />
    </>
  );
}

function ResolveTicketModal({
  action,
  error,
  form,
  mode,
  onChange,
  onClose,
  onSubmit,
  ticket,
}: {
  action: string;
  error: string;
  form: ResolutionForm;
  mode: ResolveMode;
  onChange: React.Dispatch<React.SetStateAction<ResolutionForm>>;
  onClose: () => void;
  onSubmit: (event: React.FormEvent) => void;
  ticket: SupportTicket;
}) {
  function updateField<K extends keyof ResolutionForm>(key: K, value: ResolutionForm[K]) {
    onChange((current) => ({ ...current, [key]: value }));
  }

  function toggleTag(tag: SupportResolutionTag) {
    onChange((current) => {
      const hasTag = current.tags.includes(tag);
      if (hasTag) return { ...current, tags: current.tags.filter((item) => item !== tag) };
      if (current.tags.length >= 6) return current;
      return { ...current, tags: [...current.tags, tag] };
    });
  }

  return (
    <div className="sd-resolution-modal-overlay" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="sd-resolution-modal" role="dialog" aria-modal="true" aria-labelledby="sd-resolution-title">
        <header>
          <div>
            <span>{mode === 'closed' ? 'Resolve and close' : 'Resolve ticket'}</span>
            <h2 id="sd-resolution-title">{getTicketNumber(ticket)} resolution summary</h2>
            <p>Capture what happened and what was fixed so this ticket can become useful support knowledge later.</p>
          </div>
          <button onClick={onClose} type="button">Close</button>
        </header>

        <form className="sd-resolution-form" onSubmit={onSubmit}>
          {error && <div className="sd-inline-alert is-danger">{error}</div>}

          <label className="sd-field">
            <span>Customer Issue</span>
            <textarea
              rows={3}
              value={form.customer_issue}
              onChange={(event) => updateField('customer_issue', event.target.value)}
              placeholder="Short summary of what the customer reported."
            />
          </label>

          <div className="sd-resolution-form-grid">
            <label className="sd-field">
              <span>Root Cause</span>
              <textarea
                rows={4}
                required
                value={form.root_cause}
                onChange={(event) => updateField('root_cause', event.target.value)}
                placeholder="What actually caused the issue?"
              />
            </label>
            <label className="sd-field">
              <span>Fix Applied</span>
              <textarea
                rows={4}
                required
                value={form.fix_applied}
                onChange={(event) => updateField('fix_applied', event.target.value)}
                placeholder="What did support or engineering change?"
              />
            </label>
          </div>

          <label className="sd-field">
            <span>Customer-Facing Resolution</span>
            <textarea
              rows={4}
              required
              value={form.customer_resolution}
              onChange={(event) => updateField('customer_resolution', event.target.value)}
              placeholder="Clear explanation the customer can safely read in their ticket thread and email."
            />
          </label>

          <div className="sd-resolution-form-grid">
            <label className="sd-field">
              <span>Prevention Note</span>
              <textarea
                rows={3}
                value={form.prevention_note}
                onChange={(event) => updateField('prevention_note', event.target.value)}
                placeholder="Optional note that may prevent the same issue."
              />
            </label>
            <label className="sd-field">
              <span>Internal Notes</span>
              <textarea
                rows={3}
                value={form.internal_notes}
                onChange={(event) => updateField('internal_notes', event.target.value)}
                placeholder="Staff-only context. Tenants cannot see this."
              />
            </label>
          </div>

          <div className="sd-resolution-tags-field">
            <span>Tags</span>
            <div>
              {RESOLUTION_TAGS.map((tag) => (
                <button
                  aria-pressed={form.tags.includes(tag.value)}
                  className={form.tags.includes(tag.value) ? 'is-selected' : ''}
                  key={tag.value}
                  onClick={() => toggleTag(tag.value)}
                  type="button"
                >
                  {tag.label}
                </button>
              ))}
            </div>
            <small>Select up to 6 tags for future filtering and knowledge-base training.</small>
          </div>

          <label className="sd-resolution-kb-toggle">
            <input
              checked={form.kb_candidate}
              onChange={(event) => updateField('kb_candidate', event.target.checked)}
              type="checkbox"
            />
            <span>
              <strong>Mark as knowledge-base candidate</strong>
              <small>Useful for future help articles, LLM retrieval, or repeated customer issues.</small>
            </span>
          </label>

          <footer>
            <button className="sd-secondary-button" onClick={onClose} type="button">Cancel</button>
            <button className="sd-primary-button" disabled={action === 'resolve'} type="submit">
              {action === 'resolve' ? 'Saving...' : mode === 'closed' ? 'Save Resolution and Close' : 'Save Resolution'}
            </button>
          </footer>
        </form>
      </section>
    </div>
  );
}

function TicketQueueList({
  onSelect,
  selectedId,
  tickets,
}: {
  onSelect: (id: string) => void;
  selectedId: string;
  tickets: SupportTicket[];
}) {
  return (
    <div className="sd-command-ticket-list">
      {tickets.map((ticket) => (
        <button
          className={ticket.id === selectedId ? 'is-active' : ''}
          key={ticket.id}
          onClick={() => onSelect(ticket.id)}
          type="button"
        >
          <span className="sd-command-ticket-top">
            <strong>{getTicketNumber(ticket)}</strong>
            <PriorityBadge priority={ticket.priority} />
          </span>
          <span className="sd-command-ticket-title">{ticket.subject}</span>
          <span className="sd-command-ticket-meta">
            <span>{ticket.tenant_name || 'Tenant'}</span>
            <span>{STATUS_COPY[ticket.status]}</span>
            <span>{formatDate(ticket.updated_at)}</span>
          </span>
        </button>
      ))}
    </div>
  );
}

function Stat({ detail, label, tone, value }: { detail: string; label: string; tone: 'blue' | 'danger' | 'green' | 'warning'; value: number }) {
  return (
    <article className={`sd-command-stat is-${tone}`}>
      <span>{label}</span>
      <strong>{value.toLocaleString()}</strong>
      <small>{detail}</small>
    </article>
  );
}

function ContextRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function PriorityBadge({ priority }: { priority: SupportTicketPriority }) {
  return (
    <span className={`sd-priority-badge is-${priority}`}>
      <svg viewBox="0 0 24 24" focusable="false">
        <path d="M5 21V5" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
        <path d="M5 5h11l-1.5 4L16 13H5" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
      </svg>
      {PRIORITY_COPY[priority]}
    </span>
  );
}

function SupportMessage({
  message,
  onPreview,
}: {
  message: SupportTicketThread['messages'][number];
  onPreview: (url: string, title: string) => void;
}) {
  return (
    <div className={`sd-support-message ${message.is_superadmin ? 'is-support' : ''}`}>
      <div>
        <strong>{message.is_superadmin ? 'Seedronics Support' : message.author_name || message.author_email || 'Tenant user'}</strong>
        <span>{formatDate(message.created_at)}</span>
      </div>
      <p>{message.body}</p>
      {!!message.attachments?.length && (
        <div className="sd-support-message-attachments">
          {message.attachments.map((url) => {
            const assetUrl = getAssetUrl(url) || url;
            const title = url.split('/').pop() || 'Support attachment';
            return (
              <button key={url} onClick={() => onPreview(url, title)} type="button">
                <img src={assetUrl} alt="Support attachment" />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function EmptyDeskState({ compact = false, detail, title }: { compact?: boolean; detail: string; title: string }) {
  return (
    <div className={compact ? 'sd-command-empty is-compact' : 'sd-command-empty'}>
      <strong>{title}</strong>
      <p>{detail}</p>
    </div>
  );
}

function makeResolutionForm(ticket: SupportTicket, resolution?: SupportTicketResolution | null): ResolutionForm {
  const tags = resolution?.tags;
  return {
    customer_issue: resolution?.customer_issue || ticket.subject,
    root_cause: resolution?.root_cause || '',
    fix_applied: resolution?.fix_applied || '',
    prevention_note: resolution?.prevention_note || '',
    internal_notes: resolution?.internal_notes || '',
    customer_resolution: resolution?.customer_resolution || '',
    tags: Array.isArray(tags) ? tags : [],
    kb_candidate: resolution?.kb_candidate === true,
  };
}

function getTicketNumber(ticket: Pick<SupportTicket, 'created_at' | 'id' | 'ticket_number'>) {
  if (ticket.ticket_number) return ticket.ticket_number;
  const year = ticket.created_at ? new Date(ticket.created_at).getFullYear() : new Date().getFullYear();
  return `SUP-${year}-${ticket.id.slice(0, 8).toUpperCase()}`;
}

function formatDate(value?: string | null) {
  if (!value) return '-';
  return new Date(value).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
